import { faBookmark } from '@fortawesome/pro-solid-svg-icons/faBookmark';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { type FC, Fragment, useState } from 'react';
import { ButtonGroup, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import type { DealerBoothListElementDto } from '@furality/fox-client/models';
import { useMutation } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '@/components/FontAwesomeIcon.tsx';
import LinkButton from '@/components/LinkButton.tsx';

import TooltipHelper from '../TooltipHelper.tsx';

type BoothCardProps = {
  booth: DealerBoothListElementDto;
  index: number;
  handleTagsClick: (tag: string) => void;
  setBoothBookmark?: (boothId: number, bookmarked: boolean) => void;
  setLocateBooth: (index: number) => void;
};

const BoothCard: FC<BoothCardProps> = ({
  booth,
  index,
  handleTagsClick,
  setBoothBookmark,
  setLocateBooth,
}) => {
  const [bookmarked, setBookmarked] = useState(booth.bookmarked);

  const overflowTagsCount = booth.tags.length - 3;

  const bookmarkBooth = useMutation({
    mutationFn: (id: number) =>
      fox.value
        .dealerBookmark({
          params: {
            boothId: id,
          },
        })
        .then(() => {
          setBookmarked(true);
          setBoothBookmark?.(index, true);
        }),
  });

  const unbookmarkBooth = useMutation({
    mutationFn: (id: number) =>
      fox.value
        .dealerUnbookmark({
          params: {
            boothId: id,
          },
        })
        .then(() => {
          setBookmarked(false);
          setBoothBookmark?.(index, false);
        }),
  });

  const { t } = useTranslation(['common', 'avatar_showcase', 'dealers']);

  return (
    <Card
      className={clsx('hover-shadow-lg mb-2 h-100 overflow-hidden', {
        'border-3 border-warning': bookmarked,
      })}
      style={{ transitionDuration: '0s' }}
      id={`booth-${booth.boothId}`}
    >
      {booth.bannerImage && (
        <div className="media">
          <Link title={t('view_booth')} to="/explore/dealers" search={{ id: booth.boothId }}>
            <img
              className="img-fluid pt-0"
              src={booth.bannerImage}
              alt={`Banner for ${booth.name}`}
              style={{ cursor: 'pointer' }}
            />
          </Link>
        </div>
      )}
      <Card.Body className="pb-0">
        <div className="pb-0">
          <div className="d-flex align-items-center gap-2 mb-2">
            <Link title={t('view_booth')} to="/explore/dealers" search={{ id: booth.boothId }}>
              <h5 className="m-0">{booth.name}</h5>
            </Link>
          </div>
          {booth.tags.map((tag, i) => (
            <Fragment key={tag}>
              {i < 3 && (
                <TooltipHelper
                  id={`booth-tag-${booth.boothId}`}
                  title={t('dealers:booth.tag.tooltip')}
                  child={
                    <button
                      className="badge badge-pill badge-secondary bg-dark mr-1 text-white d-inline-block border-0"
                      onClick={() => handleTagsClick(tag)}
                      style={{ fontSize: '0.75rem' }}
                    >
                      {(t(
                        `dealers:tags.${tag.toLowerCase().replaceAll(' ', '-')}` as Parameters<
                          typeof t
                        >[0],
                      ) as string) || tag}
                    </button>
                  }
                />
              )}
              {i === 3 && (
                <span
                  className="badge badge-pill badge-secondary mr-1 text-white d-inline-block "
                  style={{ fontSize: '0.75rem' }}
                >
                  {t('plus_tags_more', { overflowTagsCount: overflowTagsCount })}
                </span>
              )}
            </Fragment>
          ))}
          {booth.shortDescription && booth.shortDescription !== 'missing' ? (
            <p className="mt-3">{booth.shortDescription}</p>
          ) : (
            <div className="mt-3" />
          )}
        </div>
      </Card.Body>

      <Card.Footer className="px-0 py-0">
        <ButtonGroup className="d-flex px-0 py-0" style={{ gap: '2px' }} data-toggle="buttons">
          {setBoothBookmark &&
            (bookmarked ? (
              <TooltipHelper
                id={`booth-unbookmark-${booth.boothId}`}
                title={t('dealers:booth.bookmark.remove.tooltip')}
                child={
                  <Button
                    variant="warning"
                    className="rounded-0 px-2"
                    onClick={() => unbookmarkBooth.mutate(booth.boothId)}
                  >
                    <FontAwesomeIcon icon={faBookmark} />
                  </Button>
                }
              />
            ) : (
              <TooltipHelper
                id={`booth-bookmark-${booth.boothId}`}
                title={t('dealers:booth.bookmark.add.tooltip')}
                child={
                  <Button
                    variant="dark"
                    className="rounded-0 px-2"
                    onClick={() => bookmarkBooth.mutate(booth.boothId)}
                  >
                    <FontAwesomeIcon icon={faBookmark} />
                  </Button>
                }
              />
            ))}

          <TooltipHelper
            id={`booth-locate-${booth.boothId}`}
            title={t('dealers:booth.locate.tooltip')}
            child={
              <Button
                variant="dark"
                className="rounded-0 px-2"
                style={{ flex: '2' }}
                onClick={() => setLocateBooth(booth.boothId)}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {booth.boothId}
              </Button>
            }
          />
          <LinkButton
            to="/explore/dealers"
            search={{ id: booth.boothId }}
            variant="dark"
            className="rounded-0 px-2"
            style={{ flex: '5' }}
          >
            {t('view_booth')}
            <small className="ms-2">
              <FontAwesomeIcon icon={faChevronRight} />
            </small>
          </LinkButton>
        </ButtonGroup>
      </Card.Footer>
    </Card>
  );
};

export default BoothCard;
